/**
 * Feb 2026 · Team Roll-up Widget
 *
 * "My team's active tasks" tile shown on /app/tasks for managers with
 * the `task.view_reports_tree` permission. Rolls up all active tasks
 * (status ≠ done/cancelled) across the manager's direct + indirect
 * reports subtree so leadership can spot overload / bottlenecks at a
 * glance without opening every user's list.
 *
 * Feb 2026 update · "See as manager" — workspace owners/admins can pick
 * any other manager and view the roll-up from their perspective. The
 * subtree also propagates via `onAsManagerChange` so the Tasks list
 * below can filter to the impersonated scope.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Users, ChevronDown, ChevronRight, AlertTriangle, Clock, Calendar as CalIcon, RefreshCw, Eye } from "lucide-react";
import ManagerTreePicker from "@/components/tasks/ManagerTreePicker";

export default function TeamRollupWidget({ wsId, onFilterAssignees, onAsManagerChange, filteredKpis = null, filteredTotal = null, scopeTotal = null, rawTotal = null, onKpiClick = null, activeKpi = null }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem("tasks.rollup.expanded") !== "0"; } catch { return true; }
  });
  // Feb 2026 · KPI mode — show filtered counts (what the list below is
  // actually showing) or team totals (team-wide, pre-filter). User
  // feedback: the top tiles saying 3981 while the table shows 656
  // was confusing. Default to "filtered" so what the user sees in the
  // table matches the numbers; power users can flip to team-wide to
  // compare.  Persisted per workspace.
  const kpiModeKey = useMemo(() => `tasks.rollup.kpiMode.${wsId}`, [wsId]);
  const [kpiMode, setKpiMode] = useState(() => {
    try { return localStorage.getItem(kpiModeKey) || "filtered"; } catch { return "filtered"; }
  });
  useEffect(() => {
    try { localStorage.setItem(kpiModeKey, kpiMode); } catch { /* noop */ }
  }, [kpiMode, kpiModeKey]);
  // Feb 2026 · "+ N more reports" was hiding the long tail of
  // reportees. Users with a wide tree couldn't see everyone without
  // leaving the page. Click to expand inline.
  const [showAllPeople, setShowAllPeople] = useState(false);
  useEffect(() => {
    try { localStorage.setItem("tasks.rollup.expanded", expanded ? "1" : "0"); } catch { /* noop */ }
  }, [expanded]);
  // Feb 2026 · View-as-manager. "self" (default) or another user's id.
  const asManagerKey = useMemo(() => `tasks.rollup.asManager.${wsId}`, [wsId]);
  const [asManagerId, setAsManagerId] = useState(() => {
    try { return localStorage.getItem(asManagerKey) || "self"; } catch { return "self"; }
  });
  const [managers, setManagers] = useState([]);
  useEffect(() => {
    try {
      if (asManagerId && asManagerId !== "self") localStorage.setItem(asManagerKey, asManagerId);
      else localStorage.removeItem(asManagerKey);
    } catch { /* noop */ }
  }, [asManagerId, asManagerKey]);
  // Reset when workspace switches.
  useEffect(() => {
    try { setAsManagerId(localStorage.getItem(`tasks.rollup.asManager.${wsId}`) || "self"); } catch { setAsManagerId("self"); }
  }, [wsId]);

  const load = async () => {
    if (!wsId) return;
    setBusy(true);
    try {
      const params = {};
      if (asManagerId && asManagerId !== "self") params.as_user_id = asManagerId;
      const { data } = await api.get(`/workspaces/${wsId}/tasks-rollup`, { params });
      setData(data);
      // Feb 2026 · Lift the impersonated manager's display name + headline
      // counts up to Tasks.jsx so it can render the thin "VIEWING AS …"
      // banner directly above the task list.
      const impersonatingNow = !!(asManagerId && asManagerId !== "self" && asManagerId !== user?.id);
      let managerName = null;
      if (impersonatingNow) {
        // `managers` is loaded by a sibling effect — fall back to the
        // scope-level fields the roll-up itself returns if the list
        // hasn't landed yet.
        const m = managers.find?.((x) => x.id === asManagerId);
        managerName = m?.name || m?.email || data?.manager_name || null;
      }
      onAsManagerChange?.({
        manager_id: data?.manager_id || null,
        manager_name: managerName,
        viewer_id: data?.viewed_by || user?.id,
        subtree_user_ids: data?.subtree_user_ids || [],
        is_impersonating: impersonatingNow,
        totals_active: data?.totals?.active || 0,
        totals_overdue: data?.totals?.overdue || 0,
      });
    } catch (e) {
      // Feb 2026 · Self-heal when the persisted "as manager" id is no
      // longer an active workspace member (deleted / transferred /
      // deactivated). Reported in prod for Relation Realtech where a
      // stale localStorage key kept replaying 404s every reload.
      const status = e?.response?.status;
      const detail = (e?.response?.data?.detail || "").toLowerCase();
      const impersonating = asManagerId && asManagerId !== "self";
      if (status === 404 && impersonating
          && detail.includes("target manager not found")) {
        try { localStorage.removeItem(asManagerKey); } catch { /* noop */ }
        setAsManagerId("self");
        onAsManagerChange?.({
          manager_id: user?.id || null,
          viewer_id: user?.id,
          subtree_user_ids: [],
          is_impersonating: false,
        });
        toast.info("The previously-selected manager is no longer in this workspace. Switched back to your own view.");
        // `load()` will re-run via the asManagerId dep in useEffect.
        return;
      }
      if (status !== 403) {
        toast.error(apiErrorMessage(e, "Team roll-up failed"));
      }
      setData(null);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, asManagerId]);

  useEffect(() => {
    if (!wsId) return;
    let cancelled = false;
    api.get(`/workspaces/${wsId}/tasks-rollup/managers`)
       .then((r) => {
         if (cancelled) return;
         const list = r?.data?.managers || [];
         setManagers(list);
         // Feb 2026 · Self-heal — if the persisted "as manager" id isn't
         // in the fresh manager list (left the workspace, deactivated,
         // etc.), silently drop it so we never fire a 404 on first
         // render. Pairs with the 404 recovery in `load()`.
         if (asManagerId && asManagerId !== "self"
             && list.length > 0
             && !list.some((m) => m.id === asManagerId)) {
           try { localStorage.removeItem(asManagerKey); } catch { /* noop */ }
           setAsManagerId("self");
         }
       })
       .catch(() => { if (!cancelled) setManagers([]); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsId]);

  const totals = data?.totals || {};
  const topPeopleAll = useMemo(() => (data?.by_person || []), [data]);
  const topPeople = useMemo(
    () => (showAllPeople ? topPeopleAll : topPeopleAll.slice(0, 6)),
    [topPeopleAll, showAllPeople]
  );
  const hasWorkload = (totals.active || 0) > 0;
  const impersonating = asManagerId && asManagerId !== "self" && asManagerId !== user?.id;
  const viewingLabel = impersonating
    ? (managers.find((m) => m.id === asManagerId)?.name
       || managers.find((m) => m.id === asManagerId)?.email
       || "another manager")
    : null;

  // Feb 2026 · Pick which numbers the big tiles show. Fallback to
  // team totals when the parent hasn't passed filtered counts (e.g.
  // the widget is embedded elsewhere).
  const useFiltered = kpiMode === "filtered" && filteredKpis;
  const kpiActive  = useFiltered ? (filteredKpis.active || 0)     : (totals.active || 0);
  const kpiOverdue = useFiltered ? (filteredKpis.overdue || 0)    : (totals.overdue || 0);
  const kpiToday   = useFiltered ? (filteredKpis.due_today || 0)  : (totals.due_today || 0);
  const kpiNext7   = useFiltered ? (filteredKpis.upcoming_7d || 0): (totals.upcoming_7d || 0);
  const teamTotalActive = totals.active || 0;

  // Still show the "view-as" picker for admins even when self has no
  // reports (so they can flip into a sub-manager's view).
  if (!data || !data.subtree_size) {
    if (managers.length > 1) {
      return (
        <div className="border border-primary/30 bg-gradient-to-br from-primary/5 to-transparent mb-3 px-2 py-1.5 sm:p-3 flex items-center gap-2 flex-wrap text-xs" data-testid="tasks-team-rollup">
          <Users className="w-4 h-4 text-primary shrink-0" />
          <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-primary">
            {impersonating ? `As · ${viewingLabel}` : "Team"}
          </span>
          <span className="text-[10px] text-muted-foreground hidden sm:inline">· nothing active in this subtree</span>
          <div className="ml-auto flex items-center gap-1.5 sm:gap-2 shrink-0">
            <span className="hidden sm:inline text-[10px] uppercase tracking-widest text-muted-foreground">View as</span>
            <ManagerTreePicker wsId={wsId} viewerUserId={user?.id} value={asManagerId} onChange={setAsManagerId} />
            {impersonating && (
              <button type="button"
                onClick={() => setAsManagerId("self")}
                className="text-[10px] text-primary hover:underline whitespace-nowrap"
                data-testid="tasks-team-rollup-viewas-clear">Reset</button>
            )}
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="border border-primary/30 bg-gradient-to-br from-primary/5 to-transparent mb-3" data-testid="tasks-team-rollup">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded((v) => !v); } }}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-primary/5 transition-colors cursor-pointer"
        data-testid="tasks-team-rollup-toggle"
      >
        <Users className="w-4 h-4 text-primary shrink-0" />
        <span className="text-xs font-semibold uppercase tracking-widest text-primary">
          {impersonating ? `Viewing as · ${viewingLabel}` : "My team's active work"}
        </span>
        <span className="text-[10px] text-muted-foreground"
              title={`${data.subtree_size} people under this manager · ${totals.active} total active tasks across the team. These numbers ignore filters and the Scope chip below.`}>
          · {data.subtree_size} people · {totals.active} active
          {totals.overdue > 0 && <span className="text-rose-700 font-semibold"> · {totals.overdue} overdue</span>}
          <span className="opacity-60"> · team-wide, not affected by filters</span>
          {data.truncated && (
            <span className="ml-1 text-[9px] uppercase tracking-widest font-semibold text-amber-700 bg-amber-100 border border-amber-300 px-1 py-0.5"
                  title={`Status + per-person breakdowns are from the first ${data.sampled_rows?.toLocaleString?.() || data.sampled_rows} rows. Total "active" count is accurate.`}
                  data-testid="tasks-team-rollup-truncated">
              Sampled
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); load(); }}
          className="ml-auto text-[10px] text-muted-foreground hover:text-primary inline-flex items-center gap-1"
          data-testid="tasks-team-rollup-refresh"
          title="Refresh roll-up">
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
        </button>
        {expanded ? <ChevronDown className="w-4 h-4 text-primary" /> : <ChevronRight className="w-4 h-4 text-primary" />}
      </div>

      {expanded && (
        <div className="border-t border-primary/20 p-3 space-y-3">
          {managers.length > 1 && (
            <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2 flex-wrap" data-testid="tasks-team-rollup-viewas">
              <Eye className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">View as</span>
              <ManagerTreePicker wsId={wsId} viewerUserId={user?.id} value={asManagerId} onChange={setAsManagerId} />
              {impersonating && (
                <button type="button"
                  onClick={() => setAsManagerId("self")}
                  className="text-[10px] text-primary hover:underline"
                  data-testid="tasks-team-rollup-viewas-clear">Reset to me</button>
              )}
            </div>
          )}

          <div data-testid="tasks-team-rollup-metrics-wrap">
            {/* Feb 2026 · Mode toggle so a manager can flip the big tiles
                between the filtered list (what's actually on screen) and
                team-wide totals (reality check for load / overdue). */}
            {filteredKpis && (
              <div className="flex items-center gap-2 mb-2" data-testid="tasks-team-rollup-kpi-mode">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Numbers</span>
                <div className="inline-flex border border-border bg-background" role="tablist">
                  <button type="button"
                          onClick={() => setKpiMode("filtered")}
                          role="tab"
                          aria-selected={kpiMode === "filtered"}
                          className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${kpiMode === "filtered" ? "bg-foreground text-background" : "hover:bg-muted"}`}
                          data-testid="tasks-team-rollup-kpi-mode-filtered"
                          title="Show counts for the filtered list below">
                    Filtered
                  </button>
                  <button type="button"
                          onClick={() => setKpiMode("team")}
                          role="tab"
                          aria-selected={kpiMode === "team"}
                          className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border-l border-border ${kpiMode === "team" ? "bg-foreground text-background" : "hover:bg-muted"}`}
                          data-testid="tasks-team-rollup-kpi-mode-team"
                          title="Show team-wide totals (not reduced by filters)">
                    Team
                  </button>
                </div>
                <span className="text-[10px] text-muted-foreground italic">
                  {kpiMode === "filtered"
                    ? "Reflects the filters + scope you have applied below."
                    : "Team-wide — ignores filters, scope and search."}
                </span>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="tasks-team-rollup-metrics">
              <MetricTile
                icon={<Clock className="w-3.5 h-3.5" />}
                label="Active"
                value={kpiActive}
                teamValue={teamTotalActive}
                mode={useFiltered ? "filtered" : "team"}
                tooltip={useFiltered
                  ? `Active tasks in the current filtered list (not done / cancelled). Team-wide total is ${teamTotalActive}.`
                  : `All active tasks across ${data?.subtree_size || 0} people — team-wide, not affected by filters.`}
                tone="border-primary/30 text-primary"
                testid="rollup-metric-active"
                kpiKey="active"
                onClick={onKpiClick}
                active={false}
              />
              <MetricTile
                icon={<AlertTriangle className="w-3.5 h-3.5" />}
                label="Overdue"
                value={kpiOverdue}
                teamValue={totals.overdue || 0}
                mode={useFiltered ? "filtered" : "team"}
                tooltip={useFiltered
                  ? `Tasks in the filtered list whose due date is before today and status is not done. Team-wide overdue is ${totals.overdue || 0}. Tap to apply the Past-due quick report.`
                  : `Team-wide overdue — due date before today, status not done. Tap to apply the Past-due quick report.`}
                tone={kpiOverdue > 0 ? "border-rose-500 bg-rose-50 text-rose-800" : "border-border text-muted-foreground"}
                testid="rollup-metric-overdue"
                kpiKey="overdue"
                onClick={onKpiClick}
                active={activeKpi === "overdue"}
              />
              <MetricTile
                icon={<CalIcon className="w-3.5 h-3.5" />}
                label="Due today"
                value={kpiToday}
                teamValue={totals.due_today || 0}
                mode={useFiltered ? "filtered" : "team"}
                tooltip={useFiltered
                  ? `Tasks in the filtered list whose due date is today. Team-wide is ${totals.due_today || 0}. Tap to apply the Due today quick report.`
                  : `Team-wide tasks due today. Tap to apply the Due today quick report.`}
                tone={kpiToday > 0 ? "border-amber-500 bg-amber-50 text-amber-800" : "border-border text-muted-foreground"}
                testid="rollup-metric-due-today"
                kpiKey="due_today"
                onClick={onKpiClick}
                active={activeKpi === "due_today"}
              />
              <MetricTile
                icon={<CalIcon className="w-3.5 h-3.5" />}
                label="Next 7 days"
                value={kpiNext7}
                teamValue={totals.upcoming_7d || 0}
                mode={useFiltered ? "filtered" : "team"}
                tooltip={useFiltered
                  ? `Tasks in the filtered list due in the next 7 days (excluding today). Team-wide is ${totals.upcoming_7d || 0}. Tap to apply the Upcoming 7d quick report.`
                  : `Team-wide upcoming within 7 days. Tap to apply the Upcoming 7d quick report.`}
                tone="border-sky-400 bg-sky-50 text-sky-800"
                testid="rollup-metric-upcoming"
                kpiKey="upcoming_7d"
                onClick={onKpiClick}
                active={activeKpi === "upcoming_7d"}
              />
            </div>

            {/* Count chain — explains how the big number became the
                small number. Each segment is a quick tooltip; the
                segments only render when the parent passed the
                relevant total, so the bar is empty-safe. */}
            {(rawTotal != null || scopeTotal != null || filteredTotal != null) && (
              <div className="mt-2 flex items-center flex-wrap gap-1 text-[10px] font-mono"
                   data-testid="tasks-team-rollup-chain">
                <ChainPill label="Team" value={teamTotalActive}
                           tip={`All active tasks across the team (${data?.subtree_size || 0} people). Not reduced by filters.`} />
                {rawTotal != null && (
                  <>
                    <ChainArrow />
                    <ChainPill label="Fetched" value={rawTotal}
                               tip="Tasks fetched into the browser for this page — the superset the scope + filters work on." />
                  </>
                )}
                {scopeTotal != null && (
                  <>
                    <ChainArrow />
                    <ChainPill label="Scope" value={scopeTotal}
                               tip="After the Scope chip (All / My tasks / Assigned to me / Watching / …) narrows the fetched list." />
                  </>
                )}
                {filteredTotal != null && (
                  <>
                    <ChainArrow />
                    <ChainPill label="Filtered" value={filteredTotal}
                               tip="After search + status/project/assignee filters + Quick report chip. This is what the table shows." emphasise />
                  </>
                )}
              </div>
            )}
          </div>

          {!hasWorkload && (
            <div className="text-[11px] italic text-muted-foreground text-center py-3">
              Nobody in this team has active work — nice and quiet!
            </div>
          )}

          {hasWorkload && (
            <>
              {(data.by_status || []).length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5" data-testid="tasks-team-rollup-status">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold pr-1"
                        title="Team-wide active tasks split by status. Sum equals the Active tile when in Team mode.">
                    Status
                  </span>
                  {data.by_status.map((s) => (
                    <span
                      key={s.status}
                      className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border border-border bg-background"
                      title={`${s.count} task${s.count === 1 ? "" : "s"} in status "${s.status.replaceAll("_", " ")}" across the team — not reduced by filters.`}
                      data-testid={`rollup-status-${s.status}`}
                    >
                      <span className="capitalize">{s.status.replaceAll("_", " ")}</span>
                      <span className="font-mono font-semibold">{s.count}</span>
                    </span>
                  ))}
                </div>
              )}

              {topPeople.length > 0 && (
                <div className="border border-border/70 divide-y divide-border" data-testid="tasks-team-rollup-people">
                  <div className="grid grid-cols-[1fr,60px,60px,60px,60px] text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/40">
                    <div className="px-2 py-1">Report</div>
                    <div className="px-2 py-1 text-right">Active</div>
                    <div className="px-2 py-1 text-right">Overdue</div>
                    <div className="px-2 py-1 text-right">Today</div>
                    <div className="px-2 py-1 text-right">Next 7d</div>
                  </div>
                  {topPeople.map((p) => (
                    <button
                      key={p.user_id}
                      type="button"
                      onClick={() => onFilterAssignees?.([p.user_id])}
                      className="w-full grid grid-cols-[1fr,60px,60px,60px,60px] text-xs hover:bg-primary/5 transition-colors text-left"
                      data-testid={`rollup-person-${p.user_id}`}
                      title={`Filter tasks to ${p.user_name}'s active work`}
                    >
                      <div className="px-2 py-1 font-medium truncate">{p.user_name}</div>
                      <div className="px-2 py-1 text-right font-mono">{p.active}</div>
                      <div className={`px-2 py-1 text-right font-mono ${p.overdue > 0 ? "text-rose-700 font-semibold" : "text-muted-foreground"}`}>{p.overdue || "—"}</div>
                      <div className={`px-2 py-1 text-right font-mono ${p.due_today > 0 ? "text-amber-700 font-semibold" : "text-muted-foreground"}`}>{p.due_today || "—"}</div>
                      <div className="px-2 py-1 text-right font-mono text-muted-foreground">{p.upcoming_7d || "—"}</div>
                    </button>
                  ))}
                  {(data.by_person || []).length > 6 && (
                    <button type="button"
                            onClick={() => setShowAllPeople((v) => !v)}
                            className="w-full px-2 py-1 text-left text-[10px] text-primary hover:bg-primary/5 italic font-semibold uppercase tracking-widest bg-muted/30 border-t border-border"
                            data-testid="tasks-team-rollup-people-toggle">
                      {showAllPeople
                        ? `Show fewer (hide ${(data.by_person || []).length - 6} report${(data.by_person || []).length - 6 === 1 ? "" : "s"})`
                        : `+ ${(data.by_person || []).length - 6} more report${(data.by_person || []).length - 6 === 1 ? "" : "s"} — tap to view all`}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function MetricTile({ icon, label, value, teamValue, mode, tooltip, tone, testid, kpiKey, onClick, active }) {
  const clickable = typeof onClick === "function" && kpiKey;
  const commonClass = `border ${tone} px-2 py-1.5 flex items-center gap-2 text-left w-full ${clickable ? "cursor-pointer hover:brightness-95 hover:shadow transition" : ""} ${active ? "ring-2 ring-offset-1 ring-offset-background ring-foreground/40" : ""}`;
  const inner = (
    <>
      <span className="opacity-70">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[9px] uppercase tracking-widest opacity-80 flex items-center gap-1">
          <span>{label}</span>
          {mode && (
            <span className={`text-[8px] font-mono normal-case px-1 py-[1px] border ${mode === "filtered" ? "border-current/40 bg-background/60" : "border-current/40 bg-background/40"}`}
                  data-testid={`${testid}-mode-badge`}>
              {mode === "filtered" ? "filtered" : "team"}
            </span>
          )}
          {active && (
            <span className="text-[8px] font-mono normal-case px-1 py-[1px] border border-current bg-current/10"
                  data-testid={`${testid}-active-badge`}
                  title="This quick report is active — tap again to clear">
              on
            </span>
          )}
        </div>
        <div className="text-lg font-mono font-bold leading-tight">{value}</div>
        {mode === "filtered" && teamValue != null && teamValue !== value && (
          <div className="text-[9px] font-mono opacity-70 leading-tight"
               data-testid={`${testid}-team-hint`}>
            of {teamValue} team-wide
          </div>
        )}
      </div>
    </>
  );
  if (clickable) {
    return (
      <button
        type="button"
        className={commonClass}
        title={tooltip || ""}
        onClick={() => onClick(kpiKey)}
        aria-pressed={!!active}
        data-testid={testid}
      >
        {inner}
      </button>
    );
  }
  return (
    <div className={commonClass} title={tooltip || ""} data-testid={testid}>
      {inner}
    </div>
  );
}

function ChainPill({ label, value, tip, emphasise }) {
  return (
    <span
      className={`inline-flex items-baseline gap-1 px-1.5 py-0.5 border rounded-none select-text ${emphasise ? "border-primary bg-primary/10 text-primary" : "border-border bg-background text-muted-foreground"}`}
      title={tip || ""}
    >
      <span className="text-[9px] uppercase tracking-widest">{label}</span>
      <span className="font-semibold tabular-nums">{value}</span>
    </span>
  );
}

function ChainArrow() {
  return <span className="text-muted-foreground/60" aria-hidden="true">→</span>;
}
